import React, { useState, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import {
  Camera,
  Upload,
  Trash2,
  Image as ImageIcon,
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  Download,
  Edit2,
  CheckCircle,
  Loader2
} from 'lucide-react';
import {
  uploadPODPhoto,
  uploadMultiplePODPhotos,
  getPODPhotos,
  getPODPhoto,
  deletePODPhoto,
  updatePODPhoto
} from '../lib/api';

const photoTypeLabels = {
  delivery: 'Delivery',
  signature: 'Signature',
  condition: 'Condition',
  location: 'Location',
  front: 'Front Side',
  back: 'Back Side',
  left: 'Left Side',
  right: 'Right Side',
  other: 'Other'
};

const photoTypeColors = {
  delivery: 'bg-green-100 text-green-800',
  signature: 'bg-blue-100 text-blue-800',
  condition: 'bg-orange-100 text-orange-800',
  location: 'bg-purple-100 text-purple-800',
  front: 'bg-teal-100 text-teal-800',
  back: 'bg-cyan-100 text-cyan-800',
  left: 'bg-indigo-100 text-indigo-800',
  right: 'bg-sky-100 text-sky-800',
  other: 'bg-gray-100 text-gray-800'
};

export const PODPhotoManager = ({ 
  dnId, 
  dnNumber,
  initialPhotos = [],
  onPhotosChange,
  readOnly = false,
  compact = false
}) => {
  const [photos, setPhotos] = useState(initialPhotos);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [editForm, setEditForm] = useState({ notes: '', photo_type: 'delivery' });
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Fetch photos
  const fetchPhotos = useCallback(async () => {
    if (!dnId) return;
    try {
      setLoading(true);
      const response = await getPODPhotos(dnId);
      setPhotos(response.data.photos || []);
      if (onPhotosChange) onPhotosChange(response.data.photos || []);
    } catch (error) {
      console.error('Failed to fetch photos:', error);
    } finally {
      setLoading(false);
    }
  }, [dnId, onPhotosChange]);

  // Handle file selection
  const handleFileSelect = async (event, photoType = 'delivery') => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    try {
      if (files.length === 1) {
        await uploadPODPhoto(dnId, files[0], photoType);
        toast.success('Photo uploaded successfully');
      } else {
        await uploadMultiplePODPhotos(dnId, files, photoType);
        toast.success(`${files.length} photos uploaded successfully`);
      }
      await fetchPhotos();
    } catch (error) {
      toast.error('Failed to upload photo(s)');
      console.error(error);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  // Handle camera capture
  const handleCameraCapture = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    try {
      await uploadPODPhoto(dnId, files[0], 'delivery');
      toast.success('Photo captured and uploaded');
      await fetchPhotos();
    } catch (error) {
      toast.error('Failed to upload photo');
      console.error(error);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  // Delete photo
  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    
    try {
      await deletePODPhoto(dnId, photoId);
      toast.success('Photo deleted');
      await fetchPhotos();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  // Open edit dialog
  const handleEditPhoto = (photo) => {
    setEditingPhoto(photo);
    setEditForm({ notes: photo.notes || '', photo_type: photo.photo_type || 'delivery' });
    setEditDialogOpen(true);
  };

  // Save edit
  const handleSaveEdit = async () => {
    try {
      await updatePODPhoto(dnId, editingPhoto.id, editForm);
      toast.success('Photo updated');
      setEditDialogOpen(false);
      await fetchPhotos();
    } catch (error) {
      toast.error('Failed to update photo');
    }
  };

  // Open lightbox
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Navigate lightbox
  const navigateLightbox = (direction) => {
    const newIndex = lightboxIndex + direction;
    if (newIndex >= 0 && newIndex < photos.length) {
      setLightboxIndex(newIndex);
    }
  };

  // Download photo
  const downloadPhoto = async (photo) => {
    try {
      const response = await getPODPhoto(dnId, photo.id, true);
      const { data, mime_type, filename } = response.data;
      
      const link = document.createElement('a');
      link.href = `data:${mime_type};base64,${data}`;
      link.download = filename || 'pod_photo.jpg';
      link.click();
    } catch (error) {
      toast.error('Failed to download photo');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (compact) {
    return (
      <div className="space-y-3">
        {/* Compact view for dialogs */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700">
            POD Photos ({photos.length})
          </span>
          {!readOnly && (
            <div className="flex gap-2">
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleCameraCapture}
                className="hidden"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleFileSelect(e, 'delivery')}
                className="hidden"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploading}
              >
                <Camera className="h-4 w-4 mr-1" />
                Camera
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <Upload className="h-4 w-4 mr-1" />
                Upload
              </Button>
            </div>
          )}
        </div>

        {uploading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Uploading...
          </div>
        )}

        {photos.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground bg-slate-50 rounded-lg">
            <ImageIcon className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No photos uploaded yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {photos.map((photo, index) => (
              <div
                key={photo.id}
                className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 cursor-pointer group"
                onClick={() => openLightbox(index)}
              >
                {photo.thumbnail ? (
                  <img
                    src={`data:image/jpeg;base64,${photo.thumbnail}`}
                    alt={photo.original_filename}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="h-6 w-6 text-slate-400" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <ZoomIn className="h-6 w-6 text-white" />
                </div>
                <Badge className={`absolute bottom-1 left-1 text-xs ${photoTypeColors[photo.photo_type] || photoTypeColors.other}`}>
                  {photoTypeLabels[photo.photo_type] || 'Other'}
                </Badge>
              </div>
            ))}
          </div>
        )}

        {/* Lightbox */}
        <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
          <DialogContent className="max-w-4xl p-0 bg-black/95">
            <div className="relative">
              {photos[lightboxIndex] && (
                <>
                  <img
                    src={`data:image/jpeg;base64,${photos[lightboxIndex].thumbnail}`}
                    alt={photos[lightboxIndex].original_filename}
                    className="w-full max-h-[80vh] object-contain"
                  />
                  <div className="absolute top-2 right-2 flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => downloadPhoto(photos[lightboxIndex])}>
                      <Download className="h-4 w-4" />
                    </Button>
                    {!readOnly && (
                      <>
                        <Button size="sm" variant="secondary" onClick={() => {
                          handleEditPhoto(photos[lightboxIndex]);
                          setLightboxOpen(false);
                        }}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => {
                          handleDeletePhoto(photos[lightboxIndex].id);
                          setLightboxOpen(false);
                        }}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="secondary" onClick={() => setLightboxOpen(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  {photos.length > 1 && (
                    <>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="absolute left-2 top-1/2 -translate-y-1/2"
                        onClick={() => navigateLightbox(-1)}
                        disabled={lightboxIndex === 0}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="absolute right-2 top-1/2 -translate-y-1/2"
                        onClick={() => navigateLightbox(1)}
                        disabled={lightboxIndex === photos.length - 1}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-white text-sm">
                    {lightboxIndex + 1} / {photos.length}
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Photo Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Photo Type</Label>
                <Select value={editForm.photo_type} onValueChange={(v) => setEditForm({...editForm, photo_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(photoTypeLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  placeholder="Add notes about this photo..."
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSaveEdit}>Save Changes</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Full view (for dedicated mobile page)
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-lg">Proof of Delivery Photos</h3>
            <p className="text-sm text-muted-foreground">{dnNumber}</p>
          </div>
          {!readOnly && (
            <div className="flex gap-2">
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleCameraCapture}
                className="hidden"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleFileSelect(e, 'delivery')}
                className="hidden"
              />
              <Button
                variant="default"
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploading}
                className="bg-green-600 hover:bg-green-700"
              >
                <Camera className="h-4 w-4 mr-2" />
                Take Photo
              </Button>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </Button>
            </div>
          )}
        </div>

        {uploading && (
          <div className="flex items-center justify-center gap-2 py-4 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Uploading photos...</span>
          </div>
        )}

        {photos.length === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
            <Camera className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-muted-foreground mb-4">No delivery photos yet</p>
            {!readOnly && (
              <Button
                variant="outline"
                onClick={() => cameraInputRef.current?.click()}
              >
                <Camera className="h-4 w-4 mr-2" />
                Take First Photo
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{photos.length} photo(s)</span>
              <Button variant="ghost" size="sm" onClick={fetchPhotos}>
                <Loader2 className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {photos.map((photo, index) => (
                <div
                  key={photo.id}
                  className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 border group"
                >
                  {photo.thumbnail ? (
                    <img
                      src={`data:image/jpeg;base64,${photo.thumbnail}`}
                      alt={photo.original_filename}
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => openLightbox(index)}
                    />
                  ) : (
                    <div 
                      className="w-full h-full flex items-center justify-center cursor-pointer"
                      onClick={() => openLightbox(index)}
                    >
                      <ImageIcon className="h-8 w-8 text-slate-400" />
                    </div>
                  )}
                  
                  {/* Overlay with actions */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-2 left-2 right-2">
                      <Badge className={`text-xs ${photoTypeColors[photo.photo_type] || photoTypeColors.other}`}>
                        {photoTypeLabels[photo.photo_type] || 'Other'}
                      </Badge>
                      <p className="text-white text-xs mt-1 truncate">
                        {formatDate(photo.uploaded_at)}
                      </p>
                    </div>
                    {!readOnly && (
                      <div className="absolute top-2 right-2 flex gap-1">
                        <Button
                          size="icon"
                          variant="secondary"
                          className="h-7 w-7"
                          onClick={(e) => { e.stopPropagation(); handleEditPhoto(photo); }}
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button
                          size="icon"
                          variant="destructive"
                          className="h-7 w-7"
                          onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photo.id); }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lightbox Dialog */}
        <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
          <DialogContent className="max-w-4xl p-0 bg-black/95 border-none">
            <div className="relative min-h-[60vh]">
              {photos[lightboxIndex] && (
                <>
                  <img
                    src={`data:image/jpeg;base64,${photos[lightboxIndex].thumbnail}`}
                    alt={photos[lightboxIndex].original_filename}
                    className="w-full max-h-[80vh] object-contain"
                  />
                  <div className="absolute top-4 right-4 flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => downloadPhoto(photos[lightboxIndex])}>
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                    <Button size="icon" variant="secondary" onClick={() => setLightboxOpen(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  {photos.length > 1 && (
                    <>
                      <Button
                        size="icon"
                        variant="secondary"
                        className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10"
                        onClick={() => navigateLightbox(-1)}
                        disabled={lightboxIndex === 0}
                      >
                        <ChevronLeft className="h-6 w-6" />
                      </Button>
                      <Button
                        size="icon"
                        variant="secondary"
                        className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10"
                        onClick={() => navigateLightbox(1)}
                        disabled={lightboxIndex === photos.length - 1}
                      >
                        <ChevronRight className="h-6 w-6" />
                      </Button>
                    </>
                  )}
                  <div className="absolute bottom-4 left-0 right-0 text-center">
                    <Badge className={`${photoTypeColors[photos[lightboxIndex].photo_type] || photoTypeColors.other}`}>
                      {photoTypeLabels[photos[lightboxIndex].photo_type] || 'Other'}
                    </Badge>
                    <p className="text-white text-sm mt-2">
                      {lightboxIndex + 1} / {photos.length}
                    </p>
                    {photos[lightboxIndex].notes && (
                      <p className="text-white/80 text-sm mt-1 max-w-md mx-auto">
                        {photos[lightboxIndex].notes}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Photo Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Photo Type</Label>
                <Select value={editForm.photo_type} onValueChange={(v) => setEditForm({...editForm, photo_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(photoTypeLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  placeholder="Add notes about this photo..."
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSaveEdit}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default PODPhotoManager;
